import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Modal } from '../../../components/common/Modal';
import { WorkOrderRoastViewer } from '../../../components/workOrder/WorkOrderRoastViewer';
import { workOrderService } from '../../../services/workOrderService';
import { useLanguage } from '../../../features/language/useLanguage';
import type { WorkOrderSummary } from '../../../types/workOrder';

interface LotWorkOrderReadOnlyModalProps {
  isOpen: boolean;
  onClose: () => void;
  workOrders: WorkOrderSummary[];
}

function formatWorkOrderDate(summary: WorkOrderSummary, locale: string): string {
  const raw = summary.actualEndDate ?? summary.orderDate;
  if (!raw) return '—';
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  return date.toLocaleString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

export const LotWorkOrderReadOnlyModal: React.FC<LotWorkOrderReadOnlyModalProps> = ({
  isOpen,
  onClose,
  workOrders,
}) => {
  const { t, languageCode } = useLanguage();
  const dateLocale = languageCode === 'id' ? 'id-ID' : 'en-US';
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);

  // Reset selection to the latest work order whenever the modal opens
  // or the caller's latest work-order id changes. Dependency is the id
  // (a primitive) instead of the array reference so parent re-renders
  // with an unchanged list don't clobber the user's history selection.
  const latestId = workOrders[0]?.id ?? null;
  const [prevKey, setPrevKey] = useState<string | null>(null);
  const currentKey = isOpen && latestId ? `${isOpen}:${latestId}` : null;
  if (currentKey && currentKey !== prevKey) {
    setPrevKey(currentKey);
    setSelectedId(latestId);
    setIsHistoryOpen(false);
  } else if (!currentKey && prevKey !== null) {
    setPrevKey(null);
  }

  const { data: fullWorkOrder, isLoading } = useQuery({
    queryKey: ['work-order', selectedId],
    queryFn: () => workOrderService.getById(selectedId as string),
    enabled: isOpen && !!selectedId,
  });

  const selectedSummary = workOrders.find((wo) => wo.id === selectedId) ?? workOrders[0];
  const modalTitle = t('purchasing.lotList.modal.workOrderReadOnly', {
    number: selectedSummary?.workOrderNumber ?? '…',
  });

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title={modalTitle} maxWidth="7xl">
        {workOrders.length >= 2 && (
          <div className="flex justify-end pb-3 border-b border-gray-200 mb-4">
            <button
              type="button"
              onClick={() => setIsHistoryOpen(true)}
              className="px-3 py-2 border border-primary-700 rounded-md text-sm font-medium transition-colors bg-primary-500/80 text-white hover:bg-primary-700"
              data-test-id="lot-wo-readonly-history-button"
            >
              {t('workOrders.viewOtherWorkOrders')}
            </button>
          </div>
        )}

        {isLoading || !fullWorkOrder ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : (
          <WorkOrderRoastViewer workOrder={fullWorkOrder} />
        )}
      </Modal>

      <div onClick={(e) => e.stopPropagation()}>
        <Modal
          isOpen={isHistoryOpen}
          onClose={() => setIsHistoryOpen(false)}
          title={t('workOrders.historyList.title')}
          maxWidth="sm"
        >
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {workOrders.map((wo) => (
              <button
                key={wo.id}
                type="button"
                onClick={() => {
                  setSelectedId(wo.id);
                  setIsHistoryOpen(false);
                }}
                className="w-full text-left px-4 py-3 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors flex items-center justify-between"
                data-test-id={`lot-wo-readonly-history-row-${wo.id}`}
              >
                <div>
                  <div className="text-sm font-medium text-gray-900">{formatWorkOrderDate(wo, dateLocale)}</div>
                  <div className="text-xs text-gray-500">
                    {wo.workOrderNumber} · {wo.inputWeight}kg → {wo.outputWeight}kg
                  </div>
                </div>
                <svg aria-hidden="true" className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            ))}
          </div>
        </Modal>
      </div>
    </>
  );
};
